// src/routes/index.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Layouts
import MainLayout from '../layouts/MainLayout';
import PublicLayout from '../layouts/PublicLayout';

// Core Pages
import Dashboard from '../pages/Dashboard';
import About from '../pages/About';
import Login from '../pages/Login';
import Register from '../pages/Register';
import CreateProfile from '../pages/CreateProfile';
import ProfileSettings from '../pages/ProfileSettings';

import PricingPage from '../pages/subscription/PricingPage';
import SubscriptionManagement from '../pages/subscription/SubscriptionManagement';
import PaymentForm from '../pages/subscription/PaymentForm';
import SubscriptionSuccess from '../pages/subscription/SubscriptionSuccess';

import BusinessPage from '../pages/BusinessPage';
import BusinessList from '../components/business/BusinessList';
import BusinessDetail from '../components/business/BusinessDetail';
import BusinessForm from '../components/business/BusinessForm';

import PlanPage from '../pages/PlanPage';
import PlanList from '../components/plans/PlanList';
import PlanForm from '../components/plans/PlanForm';
import PlanDetail from '../components/plans/PlanDetail';

import TaskPage from '../pages/TaskPage';
import TaskList from '../components/tasks/TaskList';
import TaskForm from '../components/tasks/TaskForm';
import TaskDetail from '../components/tasks/TaskDetail';

import InventoryPage from '../pages/InventoryPage';
import StockItemList from '../components/inventory/items/StockItemList';
import StockItemDetail from '../components/inventory/items/StockItemDetail';
import StockItemForm from '../components/inventory/items/StockItemForm';
import SupplierList from '../components/inventory/suppliers/SupplierList';
import SupplierDetail from '../components/inventory/suppliers/SupplierDetail';
import SupplierForm from '../components/inventory/suppliers/SupplierForm';
import PurchaseOrderList from '../components/inventory/buying/PurchaseOrderList';
import PurchaseOrderDetail from '../components/inventory/buying/PurchaseOrderDetail';
import PurchaseOrderForm from '../components/inventory/buying/PurchaseOrderForm';
import ReceivingList from '../components/inventory/receiving/ReceivingList';
import ReceivingForm from '../components/inventory/receiving/ReceivingForm';
import ReceivingDetail from '../components/inventory/receiving/ReceivingDetail';
import InventoryReportList from '../components/inventory/reports/InventoryReportList';
import InventoryReportDetail from '../components/inventory/reports/InventoryReportDetail';

import RecordPage from '../pages/RecordPage';
import RecordList from '../components/records/RecordList';
import RecordDetail from '../components/records/RecordDetail';
import RecordForm from '../components/records/RecordForm';

import FinancePage from '../pages/FinancePage';
import FinanceDashboard from '../components/finance/FinanceDashboard';
import TransactList from '../components/finance/TransactList';
import TransactForm from '../components/finance/TransactForm';
import TransactDetail from '../components/finance/TransactDetail';
import AccountsList from '../components/finance/AccountsList';
import AccountForm from '../components/finance/AccountForm';
import AccountDetail from '../components/finance/AccountDetail';

import ReportsList from '../components/finance/reports/ReportsList';
import ReportViewer from '../components/finance/reports/ReportViewer';
import ProfitLossReport from '../components/finance/reports/ProfitLossReport';
import BalanceSheetReport from '../components/finance/reports/BalanceSheetReport';
import CashFlowReport from '../components/finance/reports/CashFlowReport';
import ReceivablesAgingReport from '../components/finance/reports/ReceivablesAgingReport';
import PayablesAgingReport from '../components/finance/reports/PayablesAgingReport';
import TaxSummaryReport from '../components/finance/reports/TaxSummaryReport';
import ExpenseReport from '../components/finance/reports/ExpenseReport';

import CommunicationPage from '../pages/CommunicationPage';
import InternalMessaging from '../components/communication/InternalMessaging';
import ExternalMessaging from '../components/communication/ExternalMessaging';
import AIChat from '../components/communication/AIChat';

// Email-related component imports for routes file
import EmailTemplatesList from '../components/communication/email/EmailTemplatesList';
import EmailTemplateEditor from '../components/communication/email/EmailTemplateEditor';
import EmailSender from '../components/communication/email/EmailSender';
import EmailAnalytics from '../components/communication/email/EmailAnalytics';
import BusinessEmailSettings from '../components/communication/email/BusinessEmailSettings';
import EmailSettingsRouter from '../components/communication/email/EmailSettingsRouter';
import BusinessEmailProfileForm from '../components/communication/email/BusinessEmailProfileForm';

import Meetings from '../pages/Meetings'; // Layout for Meetings section
import MeetingList from '../components/meetings/MeetingList';
import MeetingForm from '../components/meetings/MeetingForm';
import MeetingDetails from '../components/meetings/MeetingDetails';
import AgendaForm from '../components/meetings/AgendaForm';
import MinutesCapture from '../components/meetings/MinutesCapture';

import CrmPage from '../pages/CrmPage';
import CustomerList from '../components/crm/customers/CustomerList';
import CustomerDetail from '../components/crm/customers/CustomerDetail';
import CustomerForm from '../components/crm/customers/CustomerForm';
import LeadList from '../components/crm/leads/LeadList';
import LeadForm from '../components/crm/leads/LeadForm';
import LeadDetail from '../components/crm/leads/LeadDetail';
import TicketList from '../components/crm/support/TicketList';
import TicketDetail from '../components/crm/support/TicketDetail';  
import TicketForm from '../components/crm/support/TicketForm';
import SalesPipeline from '../components/crm/sales/SalesPipeline';
import OpportunityForm from '../components/crm/sales/OpportunityForm';
import OpportunityDetail from '../components/crm/sales/OpportunityDetail';
import InteractionDetail from '../components/crm/communicate/InteractionDetail';
import InteractionForm from '../components/crm/communicate/InteractionForm';
import IntegrationList from '../components/crm/communicate/IntegrationList';

import HumanCapitalPage from '../pages/HumanCapitalPage';
import TeamList from '../components/human-capital/team/TeamList';
import TeamMemberDetail from '../components/human-capital/team/TeamMemberDetail';
import TeamMemberForm from '../components/human-capital/team/TeamMemberForm';
import JobList from '../components/human-capital/recruitment/JobList';
import JobForm from '../components/human-capital/recruitment/JobForm';
import JobDetail from '../components/human-capital/recruitment/JobDetail';

// Network Pages
import NetworkLayout from '../layouts/NetworkLayout';
import NetworkHome from '../pages/network/NetworkHome';
import PostDetail from '../pages/network/PostDetail';
import NetworkProfile from '../pages/network/NetworkProfile';
import ConnectionsPage from '../pages/network/ConnectionsPage';
import DiscoverPage from '../pages/network/DiscoverPage';
import SavedPosts from '../pages/network/SavedPosts';
import MessagesPage from '../pages/network/MessagesPage';
import TagPage from '../pages/network/TagPage';

import PayrollPage from '../pages/PayrollPage';
import MarketingPage from '../pages/MarketingPage';
import PayrollDashboard from '../components/pay/PayrollDashboard';
import PayRunList from '../components/pay/runs/PayRunList';
import PayRunForm from '../components/pay/runs/PayRunForm';
import PayslipGenerator from '../components/pay/payslips/Paylipenerator';
import EmployeePayrollList from '../components/pay/employees/EmployeePayrollList';
import PayrollSettings from '../components/pay/settings/PayrollSettings';
import EmployeePayrollForm from '../components/pay/employees/EmployeePayrollForm';
import EmployeePayrollDetail from '../components/pay/employees/EmployeePayrollDetail';
import PayslipDetail from '../components/pay/payslips/PaylipDetail';
import PayrollReports from '../components/pay/reports/PayrollReports';
import PayRunDetail from '../components/pay/runs/PayRunDetail';
import PayslipList from '../components/pay/payslips/PayslipList';
import TimesheetList from '../components/pay/timesheet/TimesheetList';

// CoFounder
import CoFounderPage from '../pages/CoFounderPage';

// Utility component for dashboard routes that require subscription
const ProtectedDashboardRoute = ({ children }) => {
  const { currentUser, userProfile, subscription, isTrialing, isDemoMode } = useAuth();
  
  // Check if user has valid access
  const hasActiveSubscription = subscription?.status === 'active';
  const hasValidTrial = isTrialing && subscription?.status === 'trialing';
  const hasValidAccess = hasActiveSubscription || hasValidTrial || isDemoMode;
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  if (!userProfile && !isDemoMode) {
    return <Navigate to="/create-profile" />;
  }
  
  if (!hasValidAccess) {
    return <Navigate to="/subscription/manage" />;
  }
  
  return children;
};

const ProtectedNetworkRoute = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();

  if (!currentUser) {
    return <Navigate to="/login" state={{ redirectTo: `${location.pathname}${location.search}${location.hash}` }} replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { currentUser, userProfile, loading, isDemoMode } = useAuth();
  
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-indigo-600 rounded-full border-t-transparent"></div>
      </div>
    );
  }
  
  return (
    <BrowserRouter>
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<About />} />
        <Route path="/about" element={<About />} />
        {/* Changed redirects to /network instead of /dashboard */}
        <Route path="/login" element={!currentUser ? <Login /> : <Navigate to="/network" />} />
        <Route path="/register" element={!currentUser ? <Register /> : <Navigate to="/network" />} />
        <Route path="/pricing" element={<PricingPage />} /> {/* Public pricing page */}
        <Route path="/subscription">
          <Route path="manage" element={<SubscriptionManagement />} />
          <Route path="payment" element={<PaymentForm />} />
          <Route path="upgrade" element={<PricingPage upgradeMode={true} />} />
          <Route path="success" element={<SubscriptionSuccess />} />
        </Route>
      </Route>
      
      <Route 
        path="/create-profile" 
        element={
          (currentUser || isDemoMode) && !userProfile ? 
            <CreateProfile /> : 
            ((currentUser || isDemoMode) ? <Navigate to="/network" /> : <Navigate to="/login" />)
        } 
      />

      {/* Protected Dashboard Routes */}
      <Route path="/" element={
        <ProtectedDashboardRoute>
          <MainLayout />
        </ProtectedDashboardRoute>
      }>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="profile" element={<ProfileSettings />} />
        <Route path="cofounder" element={<CoFounderPage />} />
        <Route path="marketing" element={<MarketingPage />} />

        <Route path="business" element={<BusinessPage />}>
          <Route index element={<BusinessList />} />
          <Route path=":id" element={<BusinessDetail />} />
          <Route path="new" element={<BusinessForm />} />
          <Route path="edit/:id" element={<BusinessForm isEditing />} />
        </Route>

        <Route path="plans" element={<PlanPage />}>
          <Route index element={<PlanList />} />
          <Route path=":id" element={<PlanDetail />} />
          <Route path="new" element={<PlanForm />} />
          <Route path="edit/:id" element={<PlanForm isEditing />} />
        </Route>

        <Route path="tasks" element={<TaskPage />}>
          <Route index element={<TaskList />} />
          <Route path=":taskId" element={<TaskDetail />} />
          <Route path="new" element={<TaskForm />} />
          <Route path="edit/:taskId" element={<TaskForm isEditing />} />
        </Route>

        {/* New Inventory routes with tabs */}
        <Route path="inventory" element={<InventoryPage />}>
          {/* Items tab */}
          <Route path="items" element={<StockItemList />} />
          <Route path="items/:itemId" element={<StockItemDetail />} />
          <Route path="items/new" element={<StockItemForm />} />
          <Route path="items/edit/:itemId" element={<StockItemForm isEditing />} />
          
          {/* Suppliers tab */}
          <Route path="suppliers" element={<SupplierList />} />
          <Route path="suppliers/:supplierId" element={<SupplierDetail />} />
          <Route path="suppliers/new" element={<SupplierForm />} />
          <Route path="suppliers/edit/:supplierId" element={<SupplierForm isEditing />} />
          
          {/* Buying/Purchase Orders tab */}
          <Route path="buying" element={<PurchaseOrderList />} />
          <Route path="buying/:poId" element={<PurchaseOrderDetail />} />
          <Route path="buying/new" element={<PurchaseOrderForm />} />
          <Route path="buying/edit/:poId" element={<PurchaseOrderForm isEditing />} />
          
          {/* Receiving tab */}
          <Route path="receiving" element={<ReceivingList />} />
          <Route path="receiving/:receivingId" element={<ReceivingDetail />} />
          <Route path="receiving/new" element={<ReceivingForm />} />
          <Route path="buying/:poId/receive" element={<ReceivingForm isFromPO={true} />} />
          
          {/* Reports tab */}
          <Route path="reports" element={<InventoryReportList />} />
          <Route path="reports/:reportId" element={<InventoryReportDetail />} />
          
          {/* Default redirect */}
          <Route index element={<Navigate to="items" replace />} />
        </Route>

        <Route path="records" element={<RecordPage />}>
          <Route index element={<RecordList />} />
          <Route path=":recordId" element={<RecordDetail />} />
          <Route path="new" element={<RecordForm />} />
          <Route path="edit/:recordId" element={<RecordForm isEditing />} />
        </Route>

        <Route path="finance" element={<FinancePage />}>
          <Route index element={<Navigate to="/finance/transactions" />} />
          <Route path="dashboard" element={<FinanceDashboard />} />
          
          {/* Transactions Routes */}
          <Route path="transactions" element={<TransactList />} />
          <Route path="new" element={<TransactForm />} />
          <Route path=":transactionId" element={<TransactDetail />} />
          <Route path="edit/:transactionId" element={<TransactForm isEditing />} />
          <Route path="duplicate/:transactionId" element={<TransactForm isDuplicating />} />
          
          {/* Accounts Routes */}
          <Route path="accounts" element={<AccountsList />} />
          <Route path="accounts/new" element={<AccountForm />} />
          <Route path="accounts/:accountId" element={<AccountDetail />} />
          <Route path="accounts/edit/:accountId" element={<AccountForm isEditing />} />
          
          {/* Reports routes */}
          <Route path="reports" element={<ReportsList />} />
          <Route path="reports/profit-loss" element={<ProfitLossReport />} />
          <Route path="reports/balance-sheet" element={<BalanceSheetReport />} />
          <Route path="reports/cash-flow" element={<CashFlowReport />} />
          <Route path="reports/receivables" element={<ReceivablesAgingReport />} />
          <Route path="reports/payables" element={<PayablesAgingReport />} />
          <Route path="reports/tax" element={<TaxSummaryReport />} />
          <Route path="reports/expenses" element={<ExpenseReport />} />
          <Route path="budgets" element={<div className="bg-white p-6 rounded-xl shadow">Budgets feature coming soon</div>} />
        </Route>

        {/* Routes for email functionality */}
        <Route path="communication" element={<CommunicationPage />}>
          <Route index element={<Navigate to="/communication/internal" />} />
          <Route path="internal" element={<InternalMessaging />} />
          <Route path="external" element={<ExternalMessaging />} />
          <Route path="ai" element={<AIChat />} />
          
          {/* Email routes */}
          <Route path="email" element={<EmailTemplatesList />} />
          <Route path="email/template/new" element={<EmailTemplateEditor />} />
          <Route path="email/template/edit/:id" element={<EmailTemplateEditor />} />
          <Route path="email/send" element={<EmailSender />} />
          <Route path="email/analytics" element={<EmailAnalytics />} />
          <Route path="email/settings/:id" element={<EmailSettingsRouter />} />
          <Route path="email/profile/new" element={<BusinessEmailProfileForm />} />
        </Route>

        {/* Meetings routes */}
        <Route path="meetings" element={<Meetings />}>
          {/* Default Meetings route shows the meeting list */}
          <Route index element={<MeetingList />} />
          <Route path="create" element={<MeetingForm />} />
          <Route path="edit/:id" element={<MeetingForm isEditing />} />
          <Route path="details/:id" element={<MeetingDetails />} />
          <Route path="agenda/:id" element={<AgendaForm />} />
          <Route path="minutes/:id" element={<MinutesCapture />} />
          <Route path="*" element={<Navigate to="/meetings" replace />} />
        </Route>

        <Route path="crm" element={<CrmPage />}>
          {/* Customers */}
          <Route path="customers" element={<CustomerList />} />
          <Route path="customers/:customerId" element={<CustomerDetail />} />
          <Route path="customers/new" element={<CustomerForm />} />
          <Route path="customers/edit/:customerId" element={<CustomerForm isEditing />} />
          
          {/* Leads */}
          <Route path="leads" element={<LeadList />} />
          <Route path="leads/:leadId" element={<LeadDetail />} />
          <Route path="leads/new" element={<LeadForm />} />
          <Route path="leads/edit/:leadId" element={<LeadForm isEditing />} />
          
          {/* Sales */}
          <Route path="sales" element={<SalesPipeline />} />
          <Route path="sales/:opportunityId" element={<OpportunityDetail />} />
          <Route path="sales/new" element={<OpportunityForm />} />
          <Route path="sales/edit/:opportunityId" element={<OpportunityForm isEditing />} />
          
          {/* Communication */}
          <Route path="communicate" element={<IntegrationList />} />
          <Route path="communicate/:interactionId" element={<InteractionDetail />} />
          <Route path="communicate/new" element={<InteractionForm />} />
          <Route path="communicate/edit/:interactionId" element={<InteractionForm isEditing />} />
          
          {/* Support & Tickets */}
          <Route path="support" element={<TicketList />} />
          <Route path="support/:ticketId" element={<TicketDetail />} />
          <Route path="support/new" element={<TicketForm />} />
          <Route path="support/edit/:ticketId" element={<TicketForm isEditing />} />
          {/* Reports */}
          <Route path="reports" element={<div>CRM Reports Coming Soon</div>} />
          
          {/* Default redirect */}
          <Route index element={<Navigate to="customers" replace />} />
        </Route>

        <Route path="human-capital" element={<HumanCapitalPage />}>
          {/* Team Management routes */}
          <Route path="team" element={<TeamList />} />
          <Route path="team/:memberId" element={<TeamMemberDetail />} />
          <Route path="team/new" element={<TeamMemberForm />} />
          <Route path="team/edit/:memberId" element={<TeamMemberForm isEditing />} />
          
          {/* Recruitment routes */}
          <Route path="recruitment" element={<JobList />} />
          <Route path="recruitment/jobs/:jobId" element={<JobDetail />} />
          <Route path="recruitment/jobs/new" element={<JobForm />} />
          <Route path="recruitment/jobs/edit/:jobId" element={<JobForm isEditing />} />
          
          {/* Default redirect */}
          <Route index element={<Navigate to="team" replace />} />
        </Route>

        <Route path="payroll" element={<PayrollPage />}>
          <Route index element={<PayrollDashboard />} />
          <Route path="runs" element={<PayRunList />} />
          <Route path="runs/new" element={<PayRunForm />} />
          <Route path="runs/:payRunId" element={<PayRunDetail />} />
          <Route path="runs/:payRunId/edit" element={<PayRunForm isEditing />} />
          <Route path="runs/:payRunId/payslips" element={<PayslipGenerator />} />
          <Route path="employees" element={<EmployeePayrollList />} />
          <Route path="employees/new" element={<EmployeePayrollForm />} />
          <Route path="employees/:payrollId" element={<EmployeePayrollDetail />} />
          <Route path="employees/:payrollId/edit" element={<EmployeePayrollForm isEditing />} />
          <Route path="payslips" element={<PayslipList />} />
          <Route path="payslips/:payslipId" element={<PayslipDetail />} />
          <Route path="timesheets" element={<TimesheetList />} />
          <Route path="reports" element={<PayrollReports />} />
          <Route path="settings" element={<PayrollSettings />} />
        </Route>
      </Route>
      
      <Route path="/network/post/:postId" element={<NetworkLayout />}>
        <Route index element={<PostDetail />} />
      </Route>

      <Route path="/network" element={
        <ProtectedNetworkRoute>
          <NetworkLayout />
        </ProtectedNetworkRoute>
      }>
        <Route index element={<NetworkHome />} />
        <Route path="profile/:businessId" element={<NetworkProfile />} />
        <Route path="connections" element={<ConnectionsPage />} />
        <Route path="discover" element={<DiscoverPage />} />
        <Route path="tag/:tagName" element={<TagPage />} />
        <Route path="saved" element={<SavedPosts />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="messages/:conversationId" element={<MessagesPage />} />
      </Route>
    </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
